/**
 * Wallet Connecting Modal
 *
 * Presentational component for the wallet connecting flow:
 * 1. "Connecting" - waiting for wallet extension approval
 * 2. "Checking" - wallet connected, checking account status
 * 3. "Connected" - success, auto-closing soon
 * 4. "Unable to Check Wallet" - access check failed
 * 5. "Not Whitelisted" - access denied, shows waitlist link + restriction info
 *
 * All steps share the same centered modal shell — title, footer content,
 * and button status/text change while the container persists, producing
 * smooth cross-step transitions.
 */
import { CrossIcon } from "@solomon-labs/design/components/icons";
import {
    Button,
    ButtonIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerTitle
} from "@solomon-labs/design/components/ui";
import React, { useCallback, useEffect, useState } from "react";

import { captureWalletEvent } from "../analytics/walletEvents";
import { walletJourney } from "../analytics/walletJourney";
import { Text } from "../components/ui/text";
import { useIsMobileViewport } from "../hooks/useIsMobileViewport";
import { truncateAddress } from "../utils/format";

import type { WalletConnectionError } from "./connectionFeedback";
import { useDelayedWalletHint } from "./hooks/useDelayedWalletHint";

const MODAL_ANIMATIONS_CSS = `
/* Modal step enter: shrink + blur + fade in on mount */
@keyframes modal-step-in {
    0%   { opacity: 0; filter: blur(4px); transform: scale(0.96); }
    100% { opacity: 1; filter: blur(0);   transform: scale(1); }
}

/* Modal step exit: shrink + blur + fade out on unmount */
@keyframes modal-step-out {
    0%   { opacity: 1; filter: blur(0);   transform: scale(1); }
    100% { opacity: 0; filter: blur(4px); transform: scale(0.96); }
}

/* Modal button: fade in + slide up from bottom */
@keyframes modal-button-up {
    0%   { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
}

/* Address reveal: fade in + slide from left (subtle, 12px offset) */
@keyframes slide-in-left {
    0%   { opacity: 0; transform: translateX(-12px); }
    100% { opacity: 1; transform: translateX(0); }
}
`;

export type ConnectingStep =
    | "connection-error"
    | "connecting"
    | "checking"
    | "connected"
    | "access-error"
    | "not-whitelisted";

interface WalletConnectingModalProps {
    step: ConnectingStep;
    connectionError?: WalletConnectionError | null;
    onRetry?: () => void;
    onChooseWallet?: () => void;
    isResettingConnection?: boolean;
    walletName: string;
    walletIcon: string;
    walletAddress?: string;
    onClose: (reason?: string) => void;
    onGoToSite: () => void;
    showGoToSite: boolean;
    minHeight?: number | null;
    onMeasure?: (node: HTMLDivElement | null) => void;
}

const STEP_CONTENT: Record<
    ConnectingStep,
    {
        title: string;
        footer: (walletName: string) => React.ReactNode;
        buttonLabel: string;
        buttonStatus: "pending" | "success" | "error";
    }
> = {
    "connection-error": {
        title: "Could not connect",
        footer: () =>
            "Open your wallet and try again, or choose another wallet.",
        buttonLabel: "Try again",
        buttonStatus: "error"
    },
    connecting: {
        title: "Connecting Wallet",
        footer: (walletName) => `Approve the connection in ${walletName}.`,
        buttonLabel: "Waiting for approval",
        buttonStatus: "pending"
    },
    checking: {
        title: "Checking Wallet",
        footer: () => "Please wait while we check your account.",
        buttonLabel: "Checking…",
        buttonStatus: "pending"
    },
    connected: {
        title: "Wallet Connected",
        footer: () => "You will now be taken to the app.",
        buttonLabel: "Connected",
        buttonStatus: "success"
    },
    "access-error": {
        title: "Unable to Check Wallet",
        footer: () =>
            "We could not verify your wallet access. Check your connection and try again.",
        buttonLabel: "Close",
        buttonStatus: "error"
    },
    "not-whitelisted": {
        title: "Not Whitelisted",
        footer: () => (
            <>
                Your wallet is not whitelisted for access. You can get added to
                the waitlist by filling out this form{" "}
                <a
                    onClick={(): void =>
                        captureWalletEvent("wallet_connect:link_clicked", {
                            ...walletJourney.context(),
                            destination: "waitlist"
                        })
                    }
                    href="https://solomonlabs.typeform.com/joinwhitelist"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-foreground-base"
                >
                    here
                </a>
                .
            </>
        ),
        buttonLabel: "Not Whitelisted",
        buttonStatus: "error"
    }
};

function ProfileSection({
    step,
    walletIcon,
    walletName,
    walletAddress,
    footer
}: {
    step: ConnectingStep;
    walletIcon: string;
    walletName: string;
    walletAddress?: string;
    footer: React.ReactNode;
}): React.ReactElement {
    const [addressWidth, setAddressWidth] = useState(0);
    const [nameWidth, setNameWidth] = useState(0);
    const addressRef = useCallback((node: HTMLSpanElement | null) => {
        if (node) setAddressWidth(node.offsetWidth);
    }, []);
    const nameRef = useCallback((node: HTMLSpanElement | null) => {
        if (node) setNameWidth(node.offsetWidth);
    }, []);

    const showAddress = step !== "connecting" && !!walletAddress;
    const widthIncrease = Math.max(0, addressWidth - nameWidth);

    const [animPhase, setAnimPhase] = useState<
        "idle" | "compensate" | "settle"
    >("idle");
    useEffect(() => {
        if (showAddress && widthIncrease > 0) {
            setAnimPhase("compensate");
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimPhase("settle");
                });
            });
        } else {
            setAnimPhase("idle");
        }
    }, [showAddress, widthIncrease]);

    const offset = animPhase === "compensate" ? widthIncrease / 2 : 0;
    const displayAddress = walletAddress
        ? truncateAddress(walletAddress, 8, 8)
        : "";

    return (
        <div className="flex flex-col items-center justify-center py-8 gap-6 flex-1 px-4">
            <span
                ref={nameRef}
                className="font-sans text-sm absolute opacity-0 pointer-events-none whitespace-nowrap"
                aria-hidden="true"
            >
                {walletName}
            </span>
            {walletAddress && (
                <span
                    ref={addressRef}
                    className="font-sans text-xl md:text-2xl leading-7 tracking-tight-lg md:tracking-tight-xl font-extralight absolute opacity-0 pointer-events-none whitespace-nowrap"
                    aria-hidden="true"
                >
                    {displayAddress}
                </span>
            )}

            <div
                className="flex gap-3 items-center"
                style={{
                    transform: `translateX(${offset}px)`,
                    transition:
                        animPhase === "settle"
                            ? "transform 400ms ease-out"
                            : "none"
                }}
            >
                <img
                    src={walletIcon}
                    alt={walletName}
                    className="w-[60px] h-[60px] shrink-0"
                />
                <div className="flex flex-col gap-1">
                    <span className="font-sans text-sm text-foreground-muted">
                        {walletName}
                    </span>
                    {showAddress && (
                        <span
                            className="font-sans text-xl md:text-2xl leading-7 tracking-tight-lg md:tracking-tight-xl font-extralight text-foreground-base"
                            style={{
                                animation: "slide-in-left 400ms ease-out both"
                            }}
                        >
                            {displayAddress}
                        </span>
                    )}
                </div>
            </div>

            <Text
                variant="small"
                role={step === "connection-error" ? "alert" : "status"}
                className="text-foreground-neutral text-center"
            >
                {footer}
            </Text>
        </div>
    );
}

export function WalletConnectingModal({
    step,
    walletName,
    walletIcon,
    walletAddress,
    connectionError,
    onRetry,
    onChooseWallet,
    isResettingConnection = false,
    onClose,
    minHeight,
    onMeasure
}: WalletConnectingModalProps): React.ReactElement {
    const baseContent = STEP_CONTENT[step];
    const content =
        step === "connection-error" && connectionError
            ? {
                  ...baseContent,
                  title: connectionError.title,
                  footer: (): string => connectionError.message
              }
            : baseContent;
    const showWaitingHelp = useDelayedWalletHint(step === "connecting", 10_000);
    const canChooseWallet =
        !connectionError?.requiresReload &&
        (step === "connection-error" || showWaitingHelp);
    const isNotWhitelisted = step === "not-whitelisted";
    const isErrorStep = isNotWhitelisted || step === "access-error";
    const isMobileViewport = useIsMobileViewport();
    const handleOpenChange = (v: boolean): void => {
        if (!v) onClose();
    };

    const header = (
        <div className="flex items-center justify-between px-4 pb-3">
            <div className="flex items-start gap-1">
                <div className="pt-[7px]">
                    <div className="w-1 h-1 bg-accent" />
                </div>
                {isMobileViewport ? (
                    <DrawerTitle
                        key={step}
                        style={{
                            animation: "modal-step-in 300ms ease-out both"
                        }}
                    >
                        {content.title}
                    </DrawerTitle>
                ) : (
                    <DialogTitle
                        key={step}
                        style={{
                            animation: "modal-step-in 300ms ease-out both"
                        }}
                    >
                        {content.title}
                    </DialogTitle>
                )}
            </div>
            {isMobileViewport ? (
                <DrawerClose
                    onClick={(): void => onClose("close_button")}
                    render={
                        <ButtonIcon
                            variant="transparent"
                            size="xl"
                            aria-label="Close"
                            icon={<CrossIcon />}
                        />
                    }
                />
            ) : (
                <DialogClose
                    onClick={(): void => onClose("close_button")}
                    render={
                        <ButtonIcon
                            variant="transparent"
                            size="xl"
                            aria-label="Close"
                            icon={<CrossIcon />}
                        />
                    }
                />
            )}
        </div>
    );

    const body = (
        <div className="flex min-h-0 flex-1 flex-col">
            <div
                className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
                style={{ scrollbarGutter: "stable" }}
            >
                <div className="px-4">
                    <div className="h-px bg-line-base" />
                </div>

                <ProfileSection
                    step={step}
                    walletIcon={walletIcon}
                    walletName={walletName}
                    walletAddress={walletAddress}
                    footer={content.footer(walletName)}
                />

                {showWaitingHelp && (
                    <div className="px-4 pb-3">
                        <Text
                            variant="small"
                            role="status"
                            className="text-foreground-neutral text-center"
                        >
                            Still waiting. Open {walletName} to check for a
                            request, or choose another wallet.
                        </Text>
                    </div>
                )}

                {isNotWhitelisted && (
                    <div
                        key="phases"
                        className="px-4 pb-3"
                        style={{
                            animation: "modal-step-in 300ms ease-out both"
                        }}
                    >
                        <div className="bg-surface-sunken border border-line-base px-3 py-2 w-full">
                            <p className="font-sans text-sm text-foreground-neutral text-center">
                                During the initial phases, access will be
                                restricted to whitelisted users. Once the
                                initial phase passes, access will be opened to
                                all.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <div
                className="shrink-0 flex flex-col gap-2 px-4 pb-4"
                style={{
                    animation: "modal-button-up 400ms ease-out 200ms both"
                }}
            >
                <Button
                    variant="raised"
                    size="lg"
                    interaction="light-bar"
                    fullWidth
                    status={
                        step === "connection-error"
                            ? "idle"
                            : content.buttonStatus
                    }
                    disabled={
                        isResettingConnection ||
                        step === "connecting" ||
                        step === "checking" ||
                        step === "connected"
                    }
                    onClick={
                        step === "connection-error"
                            ? onRetry
                            : isErrorStep
                              ? (): void => onClose("close_button")
                              : undefined
                    }
                >
                    <span
                        key={step}
                        style={{
                            animation: "modal-step-in 300ms ease-out both"
                        }}
                    >
                        {connectionError?.requiresReload
                            ? "Reload page"
                            : content.buttonLabel}
                    </span>
                </Button>
                {canChooseWallet && onChooseWallet && (
                    <Button
                        variant="transparent"
                        size="lg"
                        fullWidth
                        disabled={isResettingConnection}
                        onClick={onChooseWallet}
                    >
                        {isResettingConnection
                            ? "Cancelling request…"
                            : "Choose another wallet"}
                    </Button>
                )}
            </div>
        </div>
    );

    const measuredHeightStyle = minHeight ? { minHeight } : undefined;

    if (isMobileViewport) {
        return (
            <Drawer open onOpenChange={handleOpenChange}>
                <style
                    dangerouslySetInnerHTML={{ __html: MODAL_ANIMATIONS_CSS }}
                />
                <DrawerContent
                    ref={onMeasure}
                    aria-label={content.title}
                    className="gap-0 pt-0 overflow-hidden"
                    style={measuredHeightStyle}
                >
                    {header}
                    {body}
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <style dangerouslySetInnerHTML={{ __html: MODAL_ANIMATIONS_CSS }} />
            <DialogContent
                ref={onMeasure}
                className="w-full max-w-[384px] min-h-[336px] max-h-[calc(100dvh-32px)] md:max-h-[calc(100dvh-64px)] overflow-hidden flex flex-col gap-0 pt-4"
                style={measuredHeightStyle}
            >
                {header}
                {body}
            </DialogContent>
        </Dialog>
    );
}
